import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import { ConfigContext } from '../../contexts/config-context';
import { UserContext } from '../../contexts/userContext';
import ListingAPI from '../../axios/listing';
import LocationAPI from '../../axios/locations';
import MiscAPI from '../../axios/misc';
import { formatNumber, normalize, MIN_ROOM_IDS, PRICE_PRESETS, PRICE_UNITS } from '../main-page/home-utils';
import { FilterSheet, SheetSection } from './components/filter-sheet';
import { MessageModal } from './components/message-modal';
import { ResultCard } from './components/result-card';
import { SearchMap } from './components/search-map';
import { areaContext, areaPath, areaSummary, clearedAreaFilter, isAreaSelected, placeAsArea, useAreaSuggestions, withArea, MIN_AREA_QUERY } from './area-search';
import "../../hs-theme.css";
import "./search-page.css";

const HEADER_HEIGHT = 72;
const PAGE_SIZE = 10;
const AREA_PRESETS = [30, 50, 70, 100, 150];
const SORTS = ['-date_created', 'price', '-price', '-area'];
const VIEWS = ['split', 'list', 'map'];
// [search field, listing flag] pairs offered as one-click filters
const QUICK_FILTERS = [['additional', 'registered'], ['rest', 'terrace'], ['rest', 'lift'], ['rest', 'parking'], ['rest', 'garage'], ['rest', 'ac']];
const MAX_CITY_SUGGESTIONS = 8;
const SPLIT_MIN_WIDTH = 900;

const Chevron = ({ open }) => (
    <svg className={`hs-pill-chevron ${open ? 'open' : ''}`} width="10" height="7" viewBox="0 0 12 8" fill="none">
        <path d="M1 1.5 6 6.5 11 1.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const Check = ({ size = 16, width = 2.6 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={width} strokeLinecap="round" strokeLinejoin="round"><path d="m5 12.5 4.5 4.5L19 7.5" /></svg>
);

const SlidersIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M4 7h10M18 7h2M4 17h4M12 17h8" /><circle cx="16" cy="7" r="2" /><circle cx="10" cy="17" r="2" /></svg>
);

const FilterPill = ({ label, active, open, onToggle, panelClass = '', children }) => {
    const pillRef = useRef(null);
    const panelRef = useRef(null);

    // in an open dropdown the arrow keys move between the options or chips and a letter or digit
    // jumps to the next one starting with it ("s" again goes on from "Sve vrste" to "Stan",
    // "2" goes to "2.0+"); Enter picks it. Typing in the from/to fields is left alone.
    const onKeyDown = (e) => {
        if (!open) return;
        if (e.key === 'Escape') {
            onToggle();
            pillRef.current?.focus();
            return;
        }
        if (e.target.tagName === 'INPUT') return;
        const options = panelRef.current ? Array.from(panelRef.current.querySelectorAll('.hs-pill-opt, .hs-pill-chips button')) : [];
        if (options.length === 0) return;
        const current = options.indexOf(document.activeElement);
        let next = -1;
        if (['ArrowDown', 'ArrowUp', 'ArrowRight', 'ArrowLeft'].includes(e.key)) {
            const step = e.key === 'ArrowDown' || e.key === 'ArrowRight' ? 1 : -1;
            next = (Math.max(current, step > 0 ? -1 : 0) + step + options.length) % options.length;
        } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
            const letter = normalize(e.key);
            for (let i = 1; i <= options.length && next < 0; i++) {
                const index = (current + i + options.length) % options.length;
                if (normalize(options[index].textContent.trim()).startsWith(letter)) next = index;
            }
        }
        if (next < 0) return;
        e.preventDefault();
        options[next].focus();
    };

    return (
        <div className='hs-pill-dd' onKeyDown={onKeyDown}>
            <button ref={pillRef} className={`hs-pill ${active ? 'active' : ''} ${open ? 'open' : ''}`} onClick={onToggle}>
                {label}<Chevron open={open} />
            </button>
            {open && <div ref={panelRef} className={`hs-pill-panel ${panelClass}`}>{children}</div>}
        </div>
    );
};

// from/to number fields; the search only runs once a field is left or Enter is pressed
const RangeFields = ({ min, max, unit, fromLabel, toLabel, onApply }) => {
    const [draft, setDraft] = useState({ min, max });

    // presets and the reset button change the values from outside
    useEffect(() => setDraft({ min, max }), [min, max]);

    const apply = () => {
        let { min: from, max: to } = draft;
        if (from && to && Number(from) > Number(to)) [from, to] = [to, from];
        setDraft({ min: from, max: to });
        if (from !== min || to !== max) onApply(from, to);
    };
    const field = (key, label) => (
        <label className='hs-range-field'>
            <span>{label}</span>
            <span className='hs-range-box'>
                <input inputMode='numeric' value={draft[key]} onBlur={apply}
                    onChange={(e) => setDraft({ ...draft, [key]: e.target.value.replace(/\D/g, '') })}
                    onKeyDown={(e) => e.key === 'Enter' && e.target.blur()} />
                <em>{unit}</em>
            </span>
        </label>
    );

    return (
        <div className='hs-range'>
            {field('min', fromLabel)}
            <span className='hs-range-dash'>–</span>
            {field('max', toLabel)}
        </div>
    );
};

export const pageNumbers =(page, pageCount) => {
    const start = Math.max(1, Math.min(page - 2, pageCount - 4));
    const numbers = [];
    for (let n = start; n <= Math.min(start + 4, pageCount); n++) numbers.push(n);
    if (!numbers.includes(pageCount)) numbers.push(pageCount);
    return numbers;
};

export const SearchPage = ({ title }) => {
    const { t, i18n } = useTranslation(["results", "home", "listing"]);
    const { searchData, listingTypeCode, num_rooms_options } = useContext(ConfigContext);
    const { user } = useContext(UserContext);
    const location = useLocation();
    const navigate = useNavigate();
    const barRef = useRef(null);
    const ddRef = useRef(null);
    const sortRef = useRef(null);
    const cityInputRef = useRef(null);
    const areaPanelRef = useRef(null);
    const lang = i18n.language;
    const typeIndex = listingTypeCode[title] - 1;

    const defaults = { ...searchData, listing_type: String(typeIndex + 1) };
    // filters live in the history entry, so going back from a listing restores them
    const data = location.state?.data || defaults;
    const sort = location.state?.sort || SORTS[0];

    const [page, setPage] = useState(() => Number(sessionStorage.getItem('currentPage')) || 1);
    const [results, setResults] = useState({ listings: [], count: 0, pageCount: 1, loaded: false });
    const [places, setPlaces] = useState([]);
    const [propertyTypes, setPropertyTypes] = useState([]);
    const [dd, setDd] = useState(null);
    const [cityQuery, setCityQuery] = useState('');
    // the suggestion picked with the arrow keys or the mouse; -1 when none is
    const [activeArea, setActiveArea] = useState(-1);
    const [showQuick, setShowQuick] = useState(true);
    const [view, setView] = useState(() => window.innerWidth < SPLIT_MIN_WIDTH ? 'list' : 'split');
    const [hover, setHover] = useState(null);
    const [barHeight, setBarHeight] = useState(65);
    const [messageListing, setMessageListing] = useState(null);
    // listings the visitor hid from the map; kept only while the page is open
    const [hidden, setHidden] = useState({});
    // on phones the filters open in a sheet instead of sitting in the bar
    const [sheetOpen, setSheetOpen] = useState(false);

    const dataKey = JSON.stringify(data);
    const [areaResults, areaResultsCurrent] = useAreaSuggestions(cityQuery);

    useEffect(() => {
        LocationAPI.getListOfPlaces()
            .then(res => setPlaces(res.data))
            .catch(err => console.log(err));
    }, []);

    useEffect(() => {
        MiscAPI.getPropertyTypeOptions(lang)
            .then(res => setPropertyTypes(res.data))
            .catch(err => console.log(err));
    }, [lang]);

    useEffect(() => {
        let cancelled = false;
        sessionStorage.setItem('currentPage', page);
        ListingAPI.getListingsSearch(page, data, sort, lang, PAGE_SIZE)
            .then(res => {
                if (!cancelled) setResults({ listings: res.data.results, count: res.data.count, pageCount: res.data.page_count, loaded: true });
            })
            .catch(error => {
                console.error("Error fetching listings:", error);
                if (cancelled) return;
                // a remembered page can be past the end of a different search
                if (page > 1) setPage(1);
                else setResults({ listings: [], count: 0, pageCount: 1, loaded: true });
            });
        return () => { cancelled = true; };
    }, [dataKey, sort, page, lang]);

    useEffect(() => {
        const close = (e) => {
            if (![ddRef, sortRef].some(ref => ref.current && ref.current.contains(e.target))) setDd(null);
        };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, []);

    // a new query or a closed list starts the arrow keys from the top again
    useEffect(() => setActiveArea(-1), [cityQuery, dd]);

    // the map sticks below the filter bar, whose height changes when it wraps
    useEffect(() => {
        const el = barRef.current;
        if (!el || !window.ResizeObserver) return;
        const observer = new ResizeObserver(() => setBarHeight(el.offsetHeight));
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const applySearch = (nextData, nextSort = sort, keepDropdown = false) => {
        setPage(1);
        if (!keepDropdown) setDd(null);
        navigate(location.pathname, { replace: true, state: { data: nextData, sort: nextSort } });
    };
    const update = (patch) => applySearch({ ...data, ...patch });
    const toggle = (key) => setDd(dd === key ? null : key);

    const goToPage = (n) => {
        setPage(Math.max(1, Math.min(results.pageCount, n)));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const toggleQuick = (field, name) => {
        const current = data[field] || [];
        update({ [field]: current.includes(name) ? current.filter(o => o !== name) : [...current, name] });
    };

    // kept stable so the map popups are not rebuilt on every render
    const openMessage = useCallback((listing) => {
        if (!user) {
            navigate('/login');
            return;
        }
        setMessageListing(listing);
    }, [user, navigate]);
    const hideListing = useCallback((id) => setHidden(current => ({ ...current, [id]: true })), []);
    const closeMessage = useCallback(() => setMessageListing(null), []);
    const closeSheet = useCallback(() => setSheetOpen(false), []);

    const tabLabel = t(title, { ns: 'listing' });
    const unit = typeIndex === 0 ? '€' : `€ ${t(PRICE_UNITS[typeIndex], { ns: 'home' })}`;
    const selectedTypes = data.property_type || [];
    const typeLabel = selectedTypes.length === 1
        ? propertyTypes.find(o => o.id === selectedTypes[0])?.label
        : selectedTypes.length > 1 ? t('types_selected', { n: selectedTypes.length }) : null;
    const roomLabel = (id) => `${num_rooms_options.find(o => o.id === id)?.label}+`;
    // an empty or one-letter field offers the cities; from there on the server also looks
    // through the parts of every city, so "blok 70" finds the location directly
    const searchingAreas = cityQuery.trim().length >= MIN_AREA_QUERY;
    const selectedAreas = data.areas || [];
    const suggestions = searchingAreas
        ? areaResults
        : [...selectedAreas, ...places.filter(p => normalize(p.label).includes(normalize(cityQuery))).slice(0, MAX_CITY_SUGGESTIONS).map(placeAsArea)];
    // the list closes but the cursor stays in the field, so the next location can be typed right away
    const pickArea = (area) => {
        setCityQuery('');
        update(withArea(area, data));
    };
    // while typing, the first result is marked, since Enter picks it
    const highlightedArea = activeArea >= 0 ? activeArea : searchingAreas && areaResultsCurrent && suggestions.length > 0 ? 0 : -1;
    const onAreaKeyDown = (e) => {
        const open = dd === 'city' && suggestions.length > 0;
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            if (!open) {
                setDd('city');
                return;
            }
            const step = e.key === 'ArrowDown' ? 1 : -1;
            const next = (Math.max(highlightedArea, step > 0 ? -1 : 0) + step + suggestions.length) % suggestions.length;
            setActiveArea(next);
            // only the arrow keys scroll the list: scrolling on hover would move another row under the mouse
            areaPanelRef.current?.children[next]?.scrollIntoView({ block: 'nearest' });
        } else if (e.key === 'Enter' && open && highlightedArea >= 0) {
            e.preventDefault();
            pickArea(suggestions[highlightedArea]);
        } else if (e.key === 'Escape') {
            setDd(null);
        }
    };
    const areaLabel = selectedAreas.length > 0 ? areaSummary(selectedAreas, 1) : data.place?.label;
    const areaTitle = [selectedAreas.map(a => a.label).join(', '), data.place?.label].filter(Boolean).join(', ');
    // one chip per picked part of the city, or one for the city when it is searched as a whole
    const areaChipItems = selectedAreas.length > 0 ? selectedAreas : data.place ? [placeAsArea(data.place)] : [];
    const removeArea = (area) => update(area.value ? withArea(area, data) : clearedAreaFilter);
    const quickCount = QUICK_FILTERS.filter(([field, name]) => (data[field] || []).includes(name)).length;
    const anyFilter = dataKey !== JSON.stringify(defaults);
    const { count, pageCount, loaded } = results;
    const listings = results.listings.filter(listing => !hidden[listing.id]);
    const heading = typeLabel &&selectedTypes.length === 1 ? `${typeLabel} · ${tabLabel}` : tabLabel;
    const filterCount = [selectedTypes.length, data.min_price || data.max_price, data.min_area || data.max_area, data.min_rooms].filter(Boolean).length + quickCount;
    // "100.000 – 200.000 €", "Od 100.000 €" or "Do 200.000 €", whichever ends are set
    const rangeLabel = (min, max, rangeUnit) => min && max
        ? `${formatNumber(min)} – ${formatNumber(max)} ${rangeUnit}`
        : min ? t('from_value', { value: `${formatNumber(min)} ${rangeUnit}` })
            : max ? t('to_value', { value: `${formatNumber(max)} ${rangeUnit}` }) : null;
    const priceLabel = rangeLabel(data.min_price, data.max_price, unit);
    const areaRangeLabel = rangeLabel(data.min_area, data.max_area, 'm²');
    // typing a range leaves the dropdown open, so the other end can be filled in too
    const updateRange = (patch) => applySearch({ ...data, ...patch }, sort, true);
    const priceRange = (
        <RangeFields min={String(data.min_price || '')} max={String(data.max_price || '')} unit='€' fromLabel={t('from')} toLabel={t('to')}
            onApply={(min, max) => updateRange({ min_price: min, max_price: max })} />
    );
    const areaRange = (
        <RangeFields min={String(data.min_area || '')} max={String(data.max_area || '')} unit='m²' fromLabel={t('from')} toLabel={t('to')}
            onApply={(min, max) => updateRange({ min_area: min, max_area: max })} />
    );

    // the same controls are shown in the bar dropdowns and in the phone sheet
    const typeOptions = [{ id: null, label: t('any_type', { ns: 'home' }) }, ...propertyTypes].map(o => ({
        ...o,
        on: o.id === null ? selectedTypes.length === 0 : selectedTypes.length === 1 && selectedTypes[0] === o.id,
        select: () => update({ property_type: o.id === null ? [] : [o.id] }),
    }));
    const priceChips = (
        <div className='hs-pill-chips cols-2'>
            <button className={!data.min_price && !data.max_price ? 'on' : ''} onClick={() => update({ min_price: '', max_price: '' })}>{t('any')}</button>
            {PRICE_PRESETS[typeIndex].map(p => (
                <button key={p} className={String(p) === String(data.max_price) ? 'on' : ''}
                    onClick={() => update({ max_price: String(p) })}>{formatNumber(p)} €</button>
            ))}
        </div>
    );
    const areaChips = (
        <div className='hs-pill-chips cols-3'>
            <button className={!data.min_area && !data.max_area ? 'on' : ''} onClick={() => update({ min_area: '', max_area: '' })}>{t('any')}</button>
            {AREA_PRESETS.map(a => (
                <button key={a} className={String(a) === String(data.min_area) ? 'on' : ''}
                    onClick={() => update({ min_area: String(a) })}>{a}+ m²</button>
            ))}
        </div>
    );
    const roomChips = (
        <div className='hs-pill-chips cols-3'>
            <button className={!data.min_rooms ? 'on' : ''} onClick={() => update({ min_rooms: null })}>{t('all_rooms', { ns: 'home' })}</button>
            {MIN_ROOM_IDS.map(id => (
                <button key={id} className={id === data.min_rooms ? 'on' : ''}
                    onClick={() => update({ min_rooms: id })}>{roomLabel(id)}</button>
            ))}
        </div>
    );
    const quickButtons = QUICK_FILTERS.map(([field, name]) => {
        const on = (data[field] || []).includes(name);
        return (
            <button key={name} className={on ? 'on' : ''} onClick={() => toggleQuick(field, name)}>
                {on && <Check size={13} width={3} />}{t(name, { ns: 'listing' })}
            </button>
        );
    });

    return (
        <div className='hs hs-results'>

            <div className='hs-filterbar'>
                <div ref={barRef} className='hs-filterbar-inner'>
                    <div ref={ddRef} className='hs-filterbar-filters'>
                        <div className='hs-pill-dd hs-loc'>
                            {/* without htmlFor a click on the label would press the first control in it, the chip's × */}
                            <label className='hs-loc-field' htmlFor='hs-loc-input'>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5B6779" strokeWidth="2.2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
                                {data.place &&
                                    <span className='hs-loc-chip' title={areaTitle}><span className='hs-loc-chip-label'>{areaLabel}</span>
                                        <button onClick={(e) => { e.preventDefault(); update(clearedAreaFilter); }} aria-label={t('clear_city')}>×</button>
                                    </span>}
                                <input id='hs-loc-input' ref={cityInputRef} value={cityQuery} placeholder={data.place ? t('change_city') : t('search_area')}
                                    onFocus={() => setDd('city')} onClick={() => setDd('city')} onChange={(e) => { setCityQuery(e.target.value); setDd('city'); }}
                                    onKeyDown={onAreaKeyDown} />
                            </label>
                            {dd === 'city' && suggestions.length > 0 &&
                                <div ref={areaPanelRef} className='hs-pill-panel list areas'>
                                    {suggestions.map((area, i) => {
                                        const on = isAreaSelected(area, data);
                                        const context = areaContext(area);
                                        return (
                                            <button key={`${area.type}-${area.id}`} className={`hs-pill-opt ${on ? 'on' : ''} ${i === highlightedArea ? 'active' : ''}`}
                                                onMouseDown={(e) => e.preventDefault()} onMouseEnter={() => setActiveArea(i)} onClick={() => pickArea(area)}>
                                                <span className='hs-pill-opt-text'>
                                                    <span>{area.label}</span>
                                                    {context && <span className='hs-pill-opt-sub'>{context}</span>}
                                                </span>
                                                {on && <Check />}
                                            </button>
                                        );
                                    })}
                                </div>}
                        </div>

                        <FilterPill label={<><span className='hs-pill-key'>{t('type')}:</span>{typeLabel || t('any_type', { ns: 'home' })}</>}
                            active={selectedTypes.length > 0} open={dd === 'type'} onToggle={() => toggle('type')} panelClass='list'>
                            {typeOptions.map(o => (
                                <button key={`type-${o.id}`} className={`hs-pill-opt ${o.on ? 'on' : ''}`} onClick={o.select}>
                                    {o.label}{o.on && <Check />}
                                </button>
                            ))}
                        </FilterPill>

                        <FilterPill label={priceLabel || t('price')}
                            active={!!priceLabel} open={dd === 'price'} onToggle={() => toggle('price')}>
                            {priceRange}
                            <p className='hs-pill-heading'>{t('max_price', { ns: 'home' })}</p>
                            {priceChips}
                        </FilterPill>

                        <FilterPill label={areaRangeLabel || t('area')}
                            active={!!areaRangeLabel} open={dd === 'area'} onToggle={() => toggle('area')}>
                            {areaRange}
                            <p className='hs-pill-heading'>{t('min_area')}</p>
                            {areaChips}
                        </FilterPill>

                        <FilterPill label={data.min_rooms ? t('rooms_from', { rooms: roomLabel(data.min_rooms) }) : t('rooms')}
                            active={!!data.min_rooms} open={dd === 'rooms'} onToggle={() => toggle('rooms')}>
                            <p className='hs-pill-heading'>{t('min_rooms', { ns: 'home' })}</p>
                            {roomChips}
                        </FilterPill>

                        <button className={`hs-pill ${showQuick ? 'open' : ''}`} onClick={() => setShowQuick(!showQuick)}>
                            <SlidersIcon />
                            {t('more_filters')}
                            {quickCount > 0 && <span className='hs-pill-count'>{quickCount}</span>}
                        </button>
                    </div>
                    <div className='hs-mobile-tools'>
                        <button className={`hs-pill ${filterCount > 0 ? 'active' : ''}`} onClick={() => setSheetOpen(true)} aria-label={t('filters')}>
                            <SlidersIcon />
                            {filterCount > 0 && <span className='hs-pill-count'>{filterCount}</span>}
                        </button>
                        <button className='hs-pill hs-pill-icon' onClick={() => setView(view === 'map' ? 'list' : 'map')}
                            aria-label={t(view === 'map' ? 'view_list' : 'view_map')}>
                            {view === 'map'
                                ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M9 6h11M9 12h11M9 18h11M4.5 6h.01M4.5 12h.01M4.5 18h.01" /></svg>
                                : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="m9 4-6 2.5v13.5l6-2.5 6 2.5 6-2.5V4l-6 2.5L9 4Z" /><path d="M9 4v13.5M15 6.5V20" /></svg>}
                        </button>
                    </div>
                    <span className='hs-spacer'></span>
                    <div className='hs-view-toggle'>
                        {VIEWS.map(v => (
                            <button key={v} className={`${v === view ? 'on' : ''} view-${v}`} onClick={() => setView(v)}>{t(`view_${v}`)}</button>
                        ))}
                    </div>
                </div>
            </div>

            <main className={`hs-results-main view-${view}`}>
                <nav className='hs-crumbs'>
                    <Link to='/'>{t('home')}</Link><span>/</span>
                    {data.place
                        ? <><Link to={location.pathname}>{tabLabel}</Link><span>/</span><span className='current'>{data.place.label}</span></>
                        : <span className='current'>{tabLabel}</span>}
                </nav>
                <div className='hs-results-head'>
                    <div>
                        <h1>{heading}</h1>
                        <p className='hs-results-sub'><strong>{formatNumber(count)}</strong> {t('listings_count', { ns: 'home' })}</p>
                    </div>
                    <div ref={sortRef} className='hs-pill-dd'>
                        <button className={`hs-pill ${dd === 'sort' ? 'open' : ''}`} onClick={() => toggle('sort')}>
                            <span className='hs-pill-key'>{t('sort')}:</span>{t(`sort_${sort}`)}<Chevron open={dd === 'sort'} />
                        </button>
                        {dd === 'sort' &&
                            <div className='hs-pill-panel list right'>
                                {SORTS.map(option => (
                                    <button key={option} className={`hs-pill-opt ${option === sort ? 'on' : ''}`} onClick={() => applySearch(data, option)}>
                                        {t(`sort_${option}`)}{option === sort && <Check />}
                                    </button>
                                ))}
                            </div>}
                    </div>
                </div>

                {areaChipItems.length > 0 &&
                    <div className='hs-area-chips'>
                        {areaChipItems.map(area => (
                            <span key={`${area.type}-${area.id}`} className='hs-area-chip'>
                                <span className='hs-area-chip-label'>{areaPath(area)}</span>
                                <button onClick={() => removeArea(area)} aria-label={t('clear_city')}>×</button>
                            </span>
                        ))}
                    </div>}

                {showQuick &&
                    <div className='hs-quick'>
                        {quickButtons}
                        {anyFilter && <button className='hs-quick-reset' onClick={() => applySearch(defaults)}>{t('reset_filters')}</button>}
                    </div>}

                <div className={`hs-results-grid view-${view}`}>
                    {view !== 'map' &&
                        <div className='hs-results-list'>
                            {listings.map(listing => (
                                <ResultCard key={`result-${listing.id}`} listing={listing} active={hover === listing.id}
                                    onHover={setHover} onMessage={openMessage} />
                            ))}

                            {loaded && listings.length === 0 &&
                                <div className='hs-results-empty'>
                                    <p className='hs-results-empty-title'>{t('empty_title')}</p>
                                    <p>{t('empty_text')}</p>
                                    {anyFilter && <button onClick={() => applySearch(defaults)}>{t('reset_filters')}</button>}
                                </div>}

                            {listings.length > 0 &&
                                <div className='hs-pagination'>
                                    <span>{t('page_of', { page, count: pageCount })}</span>
                                    <div className='hs-pagination-pages'>
                                        <button className='arrow' disabled={page <= 1} onClick={() => goToPage(page - 1)} aria-label={t('prev', { ns: 'home' })}>←</button>
                                        {pageNumbers(page, pageCount).map(n => (
                                            <button key={n} className={n === page ? 'on' : ''} onClick={() => goToPage(n)}>{n}</button>
                                        ))}
                                        <button className='arrow' disabled={page >= pageCount} onClick={() => goToPage(page + 1)} aria-label={t('next', { ns: 'home' })}>→</button>
                                    </div>
                                </div>}
                        </div>}

                    {view !== 'list' &&
                        <div className='hs-results-map' style={{ top: HEADER_HEIGHT + barHeight + 14, height: `calc(100vh - ${HEADER_HEIGHT + barHeight + 38}px)` }}>
                            <SearchMap listings={results.listings} hidden={hidden} activeId={hover} onHover={setHover} layoutKey={view}
                                onMessage={openMessage} onHide={hideListing} />
                        </div>}
                </div>
            </main>

            {sheetOpen &&
                <FilterSheet title={t('filters')} closeLabel={t('close')} onClose={closeSheet}
                    footer={<>
                        <button className='hs-sheet-reset' disabled={!anyFilter} onClick={() => applySearch(defaults)}>{t('reset_filters')}</button>
                        <button className='hs-sheet-apply' onClick={closeSheet}>{t('show_results', { n: formatNumber(count) })}</button>
                    </>}>
                    <SheetSection label={t('type')}>
                        <div className='hs-pill-chips cols-2'>
                            {typeOptions.map(o => (
                                <button key={`sheet-type-${o.id}`} className={o.on ? 'on' : ''} onClick={o.select}>{o.label}</button>
                            ))}
                        </div>
                    </SheetSection>
                    <SheetSection label={`${t('price')} (${unit})`}>{priceRange}{priceChips}</SheetSection>
                    <SheetSection label={t('area')}>{areaRange}{areaChips}</SheetSection>
                    <SheetSection label={t('min_rooms', { ns: 'home' })}>{roomChips}</SheetSection>
                    <SheetSection label={t('extras')}><div className='hs-quick'>{quickButtons}</div></SheetSection>
                </FilterSheet>}

            {messageListing && <MessageModal listing={messageListing} onClose={closeMessage} />}
        </div>
    );
};
